import { memo, useCallback, useEffect, useState } from 'react'

import { Cancel, Remove } from '@mui/icons-material'
import { IconButton, ListSubheader, Skeleton, styled, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useMatchRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { AnimatePresence, motion, usePresence } from 'motion/react'

import { Collapse, collapseAnimationFactory } from '@shared/components/Collapse'
import { popInOut } from '@shared/utils/support'
import { ButtonBaseLink } from 'components/LinkedMUIComponents'
import { DynamicRouteParameter } from 'config/routing'
import { Items, useRecentPlatforms } from 'domains/_Sidebar/Sections/MediaOTT/Platforms'
import { mediaPlatformsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms'
import { mediaPlatformRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform'
import { mediaPlatformQueryKeys } from 'services/queryKeys/media'

const PlatformHeaderCollapse = styled(Collapse)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
})

const PlatformHeader = styled(ListSubheader)(({ theme }) => ({
  lineHeight: 'inherit',
  width: '100%',
  margin: theme.spacing(0.5, 0),
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.levels[900],
  display: 'flex',
  alignItems: 'center',
})) as typeof ListSubheader

const PlatformHeaderButton = styled(ButtonBaseLink)(({ theme }) => ({
  font: 'inherit',
  width: '100%',
  margin: 0,
  padding: theme.spacing(0.75, 1),
  '&:hover': {
    textDecoration: 'underline',
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minWidth: 0,
})) as typeof ButtonBaseLink

const PlatformHeaderDynamicSecondaryButton = styled(IconButton)(({ theme }) => ({
  fontSize: 14,
  margin: theme.spacing(0, 0.5),
  display: 'grid',
  placeItems: 'center',
}))

const PlatformItemsCollapse = styled(Collapse)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(1),
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}))

type NavigationProps = {
  organizationId: string
  environmentId: string
  platformId: string
}

const NavigationRaw = ({ organizationId, environmentId, platformId }: NavigationProps) => {
  const [delayedLoading, setDelayedLoading] = useState(true)

  const [isPresent, safeToRemove] = usePresence()

  const router = useRouter()
  const matchRoute = useMatchRoute()
  const navigate = useNavigate({ from: mediaPlatformRoute.fullPath })

  const [, setRecentPlatforms] = useRecentPlatforms()

  const mediaPlatformRouteMatch = matchRoute({
    to: mediaPlatformRoute.to,
    params: { organizationId, environmentId, platformId },
    fuzzy: true,
  })

  const [collapsed, setCollapsed] = useState(!mediaPlatformRouteMatch)

  const platformQuery = useQuery(mediaPlatformQueryKeys.detail({ platformId }))

  const handleCollapseCloseClick = useCallback(() => {
    if (!collapsed) {
      setCollapsed(true)
    } else {
      if (mediaPlatformRouteMatch) {
        return navigate({ to: mediaPlatformsOverviewRoute.to, params: { organizationId, environmentId } }).then(() => {
          setRecentPlatforms({ environmentId, platformId })
        })
      }
      setRecentPlatforms({ environmentId, platformId })
    }
  }, [collapsed, environmentId, navigate, organizationId, setRecentPlatforms, platformId, mediaPlatformRouteMatch])

  // Delay removal of media platform items to allow for smooth animation
  useEffect(() => {
    if (!isPresent) {
      setCollapsed(true)
      const timeoutId = setTimeout(safeToRemove, 1000)

      return () => clearTimeout(timeoutId)
    }
  }, [isPresent, safeToRemove, platformId])

  // Delay loading of media platform items to allow for smooth animation
  useEffect(() => {
    if (platformQuery.isSuccess && delayedLoading) {
      const timeoutId = setTimeout(() => {
        setDelayedLoading(false)
      }, 400)

      return () => clearTimeout(timeoutId)
    }
  }, [delayedLoading, platformQuery.isSuccess])

  // Collapse media platform when navigating to another media platform
  useEffect(() => {
    if (
      !collapsed &&
      router.state.pendingMatches?.at(-1)?.params &&
      router.state.pendingMatches!.at(-1)!.params[DynamicRouteParameter.PLATFORM_ID] &&
      router.state.pendingMatches!.at(-1)!.params[DynamicRouteParameter.PLATFORM_ID] !== platformId
    ) {
      setCollapsed(true)
    }
  }, [collapsed, router.state.pendingMatches, platformId])

  // Expand media platform when navigating to it
  useEffect(() => {
    if (
      router.state.pendingMatches?.at(-1)?.params &&
      router.state.pendingMatches!.at(-1)!.params[DynamicRouteParameter.PLATFORM_ID] === platformId &&
      router.state.matches.at(-1)?.params &&
      (!router.state.matches.at(-1)!.params[DynamicRouteParameter.PLATFORM_ID] ||
        router.state.matches.at(-1)!.params[DynamicRouteParameter.PLATFORM_ID] !== platformId)
    ) {
      setCollapsed(false)
    }
  }, [router.state.matches, router.state.pendingMatches, platformId])

  return (
    <>
      <PlatformHeaderCollapse collapsed={!isPresent} component="li">
        <PlatformHeader
          color="inherit"
          disableGutters
          component={motion.div}
          variants={collapseAnimationFactory({ opacity: 0 }, { opacity: 1 })}
          whileHover={{ translateX: 2 }}
        >
          <PlatformHeaderButton
            onClick={() => {
              if (collapsed) {
                setCollapsed(false)
              }
            }}
            to={mediaPlatformRoute.to}
            params={{ organizationId, environmentId, platformId }}
          >
            <Typography sx={{ width: '100%' }} variant="heading-base" component="span" noWrap>
              {platformQuery.isLoading ? (
                <Skeleton variant="text" animation="wave" />
              ) : platformQuery.isSuccess ? (
                platformQuery.data.name
              ) : (
                <Skeleton sx={{ bgcolor: 'error.main' }} variant="text" />
              )}
            </Typography>
          </PlatformHeaderButton>

          <PlatformHeaderDynamicSecondaryButton onClick={handleCollapseCloseClick}>
            <AnimatePresence mode="wait">
              {!collapsed ? (
                <Remove
                  fontSize="inherit"
                  component={motion.svg}
                  key="collapsed"
                  variants={popInOut}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                />
              ) : (
                <Cancel
                  fontSize="inherit"
                  component={motion.svg}
                  key="expanded"
                  variants={popInOut}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                />
              )}
            </AnimatePresence>
          </PlatformHeaderDynamicSecondaryButton>
        </PlatformHeader>
      </PlatformHeaderCollapse>

      {!delayedLoading && (
        <PlatformItemsCollapse collapsed={collapsed} component="li">
          <Items organizationId={organizationId} environmentId={environmentId} platformId={platformId} />
        </PlatformItemsCollapse>
      )}
    </>
  )
}

export const Navigation = memo(NavigationRaw)
